import React, { createContext, useState, useEffect, useContext } from 'react';
import adminApiClient from '../api/adminApiClient';
import config from '../utils/config';

// Create context
const AdminContext = createContext();

// Hook for using admin context
export const useAdmin = () => {
  return useContext(AdminContext);
};

// Admin provider component
export const AdminProvider = ({ children }) => {
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminExists, setAdminExists] = useState(true); // Default to true until we check

  // Check if admin exists
  const checkAdminExists = async () => {
    try {
      const response = await adminApiClient.get(config.ADMIN_ENDPOINTS.CHECK_EXISTS);
      setAdminExists(response.data.exists);
      return response.data.exists;
    } catch (err) {
      console.error('Error checking if admin exists:', err);
      setError(err.response?.data?.message || 'Failed to check if admin exists');
      // Default to true if there's an error (safer)
      return true;
    }
  };

  // Register first admin (only available if no admins exist)
  const registerFirstAdmin = async (adminData) => {
    try {
      setError(null);
      setIsLoading(true);
      
      // Check if admin exists first
      const exists = await checkAdminExists();
      if (exists) {
        throw new Error('Cannot register first admin because an admin already exists');
      }
      
      const response = await adminApiClient.post(
        config.ADMIN_ENDPOINTS.REGISTER_FIRST, 
        adminData
      );
      
      const { token, data } = response;
      
      // Store token and admin data
      localStorage.setItem('adminToken', token);
      localStorage.setItem('admin', JSON.stringify(data.admin));
      
      setCurrentAdmin(data.admin);
      setAdminExists(true);
      return data.admin;
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Login admin
  const loginAdmin = async (credentials) => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await adminApiClient.post(
        config.ADMIN_ENDPOINTS.LOGIN, 
        { email: credentials.email, password: credentials.password }
      );
      
      const { token, data } = response;
      
      // Store token and admin data
      localStorage.setItem('adminToken', token);
      localStorage.setItem('admin', JSON.stringify(data.admin));
      
      setCurrentAdmin(data.admin);
      return data.admin;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout admin
  const logoutAdmin = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin');
    setCurrentAdmin(null);
  };

  // Update admin profile
  const updateAdminProfile = async (adminData) => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await adminApiClient.patch(config.ADMIN_ENDPOINTS.UPDATE_PROFILE, adminData);
      
      const { data } = response;
      
      // Update stored admin data
      localStorage.setItem('admin', JSON.stringify(data.admin));
      setCurrentAdmin(data.admin);
      
      return data.admin;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Update admin password
  const updateAdminPassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await adminApiClient.patch(config.ADMIN_ENDPOINTS.UPDATE_PASSWORD, {
        currentPassword,
        newPassword
      });
      
      const { token, data } = response;
      
      // Update token and admin data
      localStorage.setItem('adminToken', token);
      localStorage.setItem('admin', JSON.stringify(data.admin));
      
      setCurrentAdmin(data.admin);
      return data.admin;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update password');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Get current admin on mount
  useEffect(() => {
    const fetchCurrentAdmin = async () => {
      // Only check if admin exists when on the admin login page
      if (window.location.pathname === '/admin/login') {
        await checkAdminExists();
      }
      
      const storedAdmin = localStorage.getItem('admin');
      const token = localStorage.getItem('adminToken');
      
      if (storedAdmin && token) {
        setCurrentAdmin(JSON.parse(storedAdmin));
        
        try {
          // Verify token by fetching current admin
          const response = await adminApiClient.get(config.ADMIN_ENDPOINTS.ME);
          const { data } = response;
          
          localStorage.setItem('admin', JSON.stringify(data.admin));
          setCurrentAdmin(data.admin);
        } catch (error) {
          // If token is invalid, logout
          if (error.response && error.response.status === 401) {
            logoutAdmin();
          }
        }
      }
      
      setIsLoading(false);
    };
    
    fetchCurrentAdmin();
  }, []);

  // Context value
  const value = {
    currentAdmin,
    isLoading,
    error,
    adminExists,
    checkAdminExists,
    registerFirstAdmin,
    loginAdmin,
    logoutAdmin,
    updateAdminProfile,
    updateAdminPassword
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext; 